import { client } from './client';
import { TipoDocumento } from '../utils/documento';

export type ProfessorType = {
	nome: string;
	sobrenome: string;
	/** Ver `utils/documento`: nem todo professor tem CPF. */
	documento: string;
	tipoDocumento: TipoDocumento;
	email: string;
	senha?: string;
	ativo?: true;
};

export type UserType = {
	nome: string;
	sobrenome: string;
	email: string;
	avatarUrl: string;
};

export type AlunoType = {
	nome: string;
	sobrenome: string;
	/** Ver `utils/documento`: nem todo aluno tem CPF. */
	documento: string;
	tipoDocumento: TipoDocumento;
	email: string;
	senha: string;
	ativo: true;
	professorId: string;
};

export type AlunoUpdateType = {
	id?: string;
	nome: string;
	sobrenome: string;
	documento: string;
	tipoDocumento: TipoDocumento;
	email: string;
};

export type TipoInformativo = 'INFORMATIVO' | 'FERIADO' | 'DATA_COMEMORATIVA' | 'NOVIDADE';

export type InformativoType = {
	titulo: string;
	imagemUrl: string;
	tipo: TipoInformativo;
	ativo: boolean;
	ordem: number;
	/** Instante ISO completo; quem resolve o fuso é o navegador, não a API. */
	inicioEm: string | null;
	fimEm: string | null;
};

export type InformativoGet = InformativoType & {
	id: string;
	criadoEm: string;
};

export type GranularidadeGrafico = 'dia' | 'semana' | 'mes';

export type GraficosDashResponse = {
	periodo: {
		de: string;
		ate: string;
		granularidade: GranularidadeGrafico;
		offsetMinutes: number;
	};
	/** Série contínua: a API preenche com zero o dia sem aula. */
	aulasPorPeriodo: { bucket: string; total: number }[];
	/** Sempre os cinco status, mesmo os que zeraram no período. */
	aulasPorStatus: { status: string; total: number }[];
	aulasPorProfessor: { professorId: string; nome: string; total: number }[];
	encerramentoPorTipo: { bucket: string; manual: number; automatica: number }[];
};

export type TransferenciaAlunoResponse = {
	message: string;
	aulasTransferidas: number;
};

export type AulaType = {
	alunoId: string;
	materialId: string;
	titulo: string;
	professorId: string;
	data: string;
	status: string;
};

export type AuthType = {
	username: string;
	password: string;
};

export type UpdatePassword = {
	email: string;
	senha: string;
	codigo: number;
};

export type MaterialType = {
	nome: string;
	descricao: string;
	driveUrl: string;
	thumbnail: string;
};

export type AulaTypePost = {
	alunoId: string;
	titulo: string;
	professorId: string;
	data: string;
	status: string;
	materialId: string;
};

export type CreateBookPost = {
	nome: string;
	descricao: string;
	capa: string;
	idioma: string;
	nivel: string;
};

export type CreateLessonPost = {
	thumbnail: string;
	nome: string;
	bookId: string;
	canvaUrl: string;
};

export type UpdateStatusAula = {
	info: string;
	timer: number;
};

export type Homework = {
	nome: string;
	materialUrl: string;
	lessonId: string;
};

export type HomeworkGet = {
	id: string;
	nome: string;
	materialUrl: string;
	lessonId: string;
};

export const adicionarProfessor = async (usuario: ProfessorType) => {
	return await client.post('/v1/professor', usuario);
};

export const adicionarAluno = async (usuario: AlunoType) => {
	return await client.post('/v1/aluno', usuario);
};

export const adicionarAula = async (aula: AulaType) => {
	return await client.post('/v1/aula', aula);
};

export const adicionarMaterial = async (material: MaterialType) => {
	return await client.post('/v1/material', material);
};

export const listarAlunos = async () => {
	return await client.get('/v1/aluno');
};

export const listarAlunosPorProfessorId = async (professorId: string) => {
	return await client.get(`/v1/aluno/professor/${professorId}`);
};
export const buscarAlunoPorId = async (id: string) => {
	return await client.get(`/v1/aluno/${id}`);
};
export const listarProfessores = async () => {
	return await client.get('/v1/professor');
};

export const listarAulasPorProfessorId = async (professorId: string) => {
	return await client.get(`/v1/aula/professor/${professorId}`);
};

export const listarAulasPorAluno = async (alunoId: any) => {
	return await client.get(`/v1/aula/aluno/${alunoId}`);
};

export const buscarAulaPorID = async (id: any) => {
	return await client.get(`/v1/aula/${id}`);
};

export const listarMateriais = async () => {
	return await client.get('/v1/material');
};

export const listarBooks = async () => {
	return await client.get('/v1/book');
};

export const listarBooksPorAluno = async (alunoId: string) => {
	return await client.get(`/v1/book/aluno/${alunoId}`);
};

export const auth = async (auth: AuthType) => {
	return await client.post('/v1/auth/login', auth);
};

export const updateUsuario = async (email: string, usuario: UserType) => {
	return await client.post(`/v1/user/update-profile/${email}`, usuario);
};

// emailAtual e a chave da rota: usar usuario.email quebraria a edicao do proprio
// email, porque o backend procuraria pelo endereco novo, que ainda nao existe.
export const updateProfessor = async (emailAtual: string, usuario: ProfessorType) => {
	return await client.post(`/v1/professor/update/${emailAtual}`, usuario);
};

export const updateAluno = async (emailAtual: string, usuario: AlunoUpdateType) => {
	return await client.post(`/v1/aluno/update/${emailAtual}`, usuario);
};

// Rota separada do update porque a troca de professor tambem repassa as aulas
// AGENDADA/PENDENTE do aluno, e porque so ADMIN pode chama-la - o update de
// aluno continua liberado para PROFESSOR. Enviar professorId no corpo do update
// leva 400: o backend usa forbidNonWhitelisted.
export const transferirAluno = async (email: string, professorId: string) => {
	return await client.post<TransferenciaAlunoResponse>(`/v1/aluno/${email}/transferir/${professorId}`);
};

export const updateMaterial = async (id: string, material: MaterialType) => {
	return await client.post(`/v1/material/update/${id}`, material);
};

export const deleteMaterial = async (id: string) => {
	return await client.delete(`/v1/material/delete/${id}`);
};

export const updateAula = async (id: string, aula: AulaTypePost) => {
	return await client.post(`/v1/aula/update/${id}`, aula);
};

export const buscarMaterialPorId = async (id: string) => {
	return await client.get(`/v1/material/${id}`);
};

export const buscarProfessorPorEmail = async (email: string) => {
	return await client.get(`/v1/professor/email/${email}`);
};
export const buscarBookPorId = async (id: string) => {
	return await client.get(`/v1/book/${id}`);
};

export const sendToken = async (email: string) => {
	return await client.post(`/v1/user/send-code`, { email });
};

export const updatePassword = async (payload: UpdatePassword) => {
	return await client.post(`/v1/user/update-password`, payload);
};

export const updateStatusAula = async (id: string, status: string, payload: UpdateStatusAula) => {
	return await client.put(`/v1/aula/update/${id}/status/${status}`, payload);
};

export const adicionarBook = async (payload: CreateBookPost) => {
	return await client.post(`/v1/book`, payload);
};

export const adicionarLesson = async (payload: CreateLessonPost) => {
	return await client.post(`/v1/lesson`, payload);
};

export const updateLesson = async (id: string, payload: CreateLessonPost) => {
	return await client.put(`/v1/lesson/${id}`, payload);
};

export const deleteBook = async (id: string) => {
	return await client.delete(`/v1/book/${id}`);
};

export const updateBook = async (id: string, payload: CreateBookPost) => {
	return await client.put(`/v1/book/${id}`, payload);
};

export const deleteLesson = async (id: string) => {
	return await client.delete(`/v1/lesson/${id}`);
};

export const disableUser = async (id: string, value: boolean) => {
	return await client.post(`/v1/aluno/${id}/block/${value}`);
};

export const addHomework = async (homework: Homework) => {
	return await client.post(`/v1/lesson/add-homework`, homework);
};

export const deleteHomework = async (id: string) => {
	return await client.delete(`/v1/lesson/homework/${id}`);
};

export const buscarAlunoPorEmail = async (email: string) => {
	return await client.get(`/v1/aluno/portal/${email}`);
};

export const deleteAluno = async (id: string) => {
	return await client.delete(`/v1/aluno/${id}`);
};

export const deleteProfessor = async (id: string) => {
	return await client.delete(`/v1/professor/${id}`);
};

export const liberarBook = async (alunoId: string, bookId: string) => {
	return await client.put(`/v1/book/${bookId}/aluno/${alunoId}`);
};

export const removerBook = async (alunoId: string, bookId: string) => {
	return await client.delete(`/v1/book/${bookId}/aluno/${alunoId}`);
};

export const dashData = async (userId: string) => {
	return await client.get(`/v1/dash/report/${userId}`);
};

/**
 * Séries dos gráficos do painel, todas numa requisição só.
 *
 * `offsetMinutes` usa o sinal do fuso, e não o do `getTimezoneOffset()`, que é
 * invertido - daí o menos na frente. A API precisa dele para saber onde o dia
 * começa: o servidor roda em UTC e sem isso a aula das 21h cairia no dia
 * seguinte.
 */
export const graficosDash = async (
	de: string,
	ate: string,
	granularidade: GranularidadeGrafico
) => {
	return await client.get<GraficosDashResponse>('/v1/dash/admin/graficos', {
		params: {
			de,
			ate,
			granularidade,
			offsetMinutes: -new Date().getTimezoneOffset()
		}
	});
};

// O mural e a lista de gestão são rotas diferentes de propósito: o aluno só
// pode ver o que está publicado e dentro da janela, o admin precisa ver tudo.
export const listarInformativosDoMural = async () => {
	return await client.get<InformativoGet[]>('/v1/informativo/mural');
};

export const listarInformativos = async () => {
	return await client.get<InformativoGet[]>('/v1/informativo');
};

export const adicionarInformativo = async (informativo: InformativoType) => {
	return await client.post<InformativoGet>('/v1/informativo', informativo);
};

export const updateInformativo = async (id: string, informativo: InformativoType) => {
	return await client.patch<InformativoGet>(`/v1/informativo/${id}`, informativo);
};

export const deleteInformativo = async (id: string) => {
	return await client.delete(`/v1/informativo/${id}`);
};
